import {autoSave} from "../../../common/autoSave.js";
import {convertToYoutubeEmbed, extractYoutubeId} from "../videoSection.js";
import l10n from "../../../../lang/locallang";

/**
 * Class handling all video section events in the editor
 */
export class VideoSectionEvents {

    run() {
        this.handleYoutubeUrlAdd();
        this.handleVideoFileUpload();
        this.handleVideoReplace();
        this.handleVideoRemove();
        this.handleThumbnailPlay();
        this.handleThumbnailUpload();
        this.handleThumbnailRemove();
        this.handleCaptionEdit();
    }

    /**
     * Handle YouTube/Vimeo URL input — triggers on Enter or paste
     */
    handleYoutubeUrlAdd() {
        // Handle Enter key
        window.addEventListener('keydown', (e) => {
            if (e.key !== 'Enter') return;
            const input = e.target.closest('.video-url-input');
            if (!input) return;
            e.preventDefault();
            this.processVideoUrl(input);
        });

        // Handle paste — auto-process after short delay
        window.addEventListener('paste', (e) => {
            const input = e.target.closest('.video-url-input');
            if (!input) return;
            setTimeout(() => this.processVideoUrl(input), 100);
        });

        // Handle blur (leaving the field)
        window.addEventListener('focusout', (e) => {
            const input = e.target.closest('.video-url-input');
            if (!input) return;
            if (input.value.trim()) {
                this.processVideoUrl(input);
            }
        });
    }

    /**
     * Process the URL from the input field
     */
    processVideoUrl(input) {
        const url = input.value.trim();
        if (!url) return;

        const container = input.closest('.video-container');
        if (!container) return;

        if (this.isValidYoutubeUrl(url) || this.isValidVimeoUrl(url)) {
            this.deleteOldVideoFromS3(container);
            this.setYoutubeVideo(container, url);
            autoSave.init(null, 1000);
        } else {
            input.classList.add('is-error');
            setTimeout(() => input.classList.remove('is-error'), 2000);
        }
    }

    /**
     * Validate Vimeo URL
     */
    isValidVimeoUrl(url) {
        return /^(https?:\/\/)?(www\.)?(vimeo\.com\/)(\d+)$/.test(url);
    }

    /**
     * Handle uploading a video file — uploads directly to S3 via XHR with progress
     */
    handleVideoFileUpload() {
        window.addEventListener('click', (e) => {
            const btn = e.target.closest('.video-upload-btn');
            if (!btn) return;
            e.preventDefault();
            const container = btn.closest('.video-container');
            const fileInput = container.querySelector('.video-file-input');
            fileInput.click();
        });

        window.addEventListener('change', (e) => {
            const fileInput = e.target.closest('.video-file-input');
            if (!fileInput) return;

            const file = fileInput.files[0];
            if (!file) return;

            // Max 500MB (same as videoLessonUpload)
            const maxSize = 500 * 1024 * 1024;
            if (file.size > maxSize) {
                alert(l10n.editor.videoFileSizeTooBig || 'File size is too big. Max file size is 500 MB');
                return;
            }

            const section = fileInput.closest('.editor-section');
            const container = fileInput.closest('.video-container');
            const componentId = section ? section.id : '';

            // Get topic_id from URL
            const urlParams = new URLSearchParams(window.location.search);
            const topicId = urlParams.get('topic') || '';

            // Check if upload URL is defined
            if (typeof uploadVideoFileURL === 'undefined') {
                console.error('uploadVideoFileURL is not defined');
                return;
            }

            // Delete old video from S3 if replacing an existing upload
            this.deleteOldVideoFromS3(container);

            // Show progress UI
            this.showUploadProgress(container, file.name);

            // Generate thumbnails from the file in parallel with upload
            this.generateVideoThumbnails(file).then(thumbnails => {
                if (thumbnails.length > 0) {
                    // Auto-set the first thumbnail
                    container.dataset.videoThumbnail = thumbnails[0];
                    // Store all generated thumbnails for the picker
                    container.dataset.videoThumbnails = JSON.stringify(thumbnails);
                }
            });

            // Build FormData
            const formData = new FormData();
            formData.append('video_file', file);
            formData.append('topic_id', topicId);
            formData.append('component_id', componentId);

            // XHR for progress tracking
            const xhr = new XMLHttpRequest();
            xhr.open('POST', uploadVideoFileURL, true);

            // CSRF token
            const csrfToken = document.querySelector('meta[name="csrf-token"]');
            if (csrfToken) {
                xhr.setRequestHeader('X-CSRF-Token', csrfToken.getAttribute('content'));
            }

            xhr.upload.addEventListener('progress', (evt) => {
                if (evt.lengthComputable) {
                    const percent = Math.round((evt.loaded / evt.total) * 100);
                    this.updateUploadProgress(container, percent);
                }
            });

            xhr.addEventListener('load', () => {
                try {
                    const response = JSON.parse(xhr.responseText);
                    if (response.result === 'success' && response.url) {
                        this.setUploadedVideo(container, response.url, response.path);
                        this.hideUploadProgress(container);
                        autoSave.init(null, 1000);
                    } else {
                        this.showUploadError(container, response.message || 'Upload failed');
                    }
                } catch (err) {
                    this.showUploadError(container, 'Upload failed');
                }
            });

            xhr.addEventListener('error', () => {
                this.showUploadError(container, 'Network error during upload');
            });

            xhr.send(formData);
        });
    }

    /**
     * Show upload progress overlay on the video container
     */
    showUploadProgress(container, fileName) {
        const figure = container.querySelector('.video-container_figure');
        const caption = figure.querySelector('.video-container_caption');
        const captionHtml = caption ? caption.outerHTML : '';

        // Replace placeholder with progress UI
        figure.innerHTML = `
            <div class="video-upload-progress">
                <div class="video-upload-progress_info">
                    <i class="icon menu-icon_video"></i>
                    <div class="video-upload-progress_text">
                        <span class="video-upload-progress_title">${l10n.editor.videoUploading || 'Uploading video...'}</span>
                        <span class="video-upload-progress_filename">${fileName}</span>
                        <span class="video-upload-progress_hint">${l10n.editor.videoUploadHint || 'You can continue editing. We\'ll notify you when it\'s ready.'}</span>
                    </div>
                </div>
                <div class="video-upload-progress_bar-wrap">
                    <div class="video-upload-progress_bar" style="width: 0%"></div>
                </div>
                <span class="video-upload-progress_percent">0%</span>
            </div>
            ${captionHtml}`;

        container.dataset.videoType = 'uploading';
    }

    /**
     * Update the progress bar percentage
     */
    updateUploadProgress(container, percent) {
        const bar = container.querySelector('.video-upload-progress_bar');
        const percentEl = container.querySelector('.video-upload-progress_percent');
        if (bar) bar.style.width = percent + '%';
        if (percentEl) percentEl.textContent = percent + '%';
    }

    /**
     * Hide progress and show success state
     */
    hideUploadProgress(container) {
        // The setUploadedVideo call already replaces the content
    }

    /**
     * Show upload error
     */
    showUploadError(container, message) {
        const progress = container.querySelector('.video-upload-progress');
        if (progress) {
            progress.classList.add('is-error');
            const title = progress.querySelector('.video-upload-progress_title');
            if (title) title.textContent = message;
            const hint = progress.querySelector('.video-upload-progress_hint');
            if (hint) hint.textContent = l10n.editor.videoUploadRetry || 'Please try again.';
            const bar = progress.querySelector('.video-upload-progress_bar');
            if (bar) bar.style.background = '#e74c3c';
        }
        // Reset after 3 seconds
        setTimeout(() => {
            this.resetToPlaceholder(container);
        }, 3000);
    }

    /**
     * Handle replacing video from sidebar settings
     */
    handleVideoReplace() {
        window.addEventListener('click', (e) => {
            const btn = e.target.closest('.video-settings-replace-btn');
            if (!btn) return;
            e.preventDefault();

            const settingsMenu = document.getElementById('settings-menu');
            const componentId = settingsMenu.dataset.component;
            const component = document.getElementById(componentId);
            if (!component) return;

            const container = component.querySelector('.video-container');
            this.deleteOldVideoFromS3(container);
            this.resetToPlaceholder(container);
            autoSave.init(null, 1000);
        });
    }

    /**
     * Handle removing video
     */
    handleVideoRemove() {
        window.addEventListener('click', (e) => {
            const btn = e.target.closest('.video-remove-btn');
            if (!btn) return;
            e.preventDefault();

            const container = btn.closest('.video-container');
            if (!container) return;

            this.deleteOldVideoFromS3(container);
            this.resetToPlaceholder(container);
            autoSave.init(null, 1000);
        });
    }

    /**
     * Delete old video and thumbnail from S3 when removing or replacing.
     * Only deletes uploaded (S3) files, not YouTube/Vimeo URLs.
     */
    deleteOldVideoFromS3(container) {
        if (!container) return;

        const videoType = container.dataset.videoType;
        const videoUrl = container.dataset.videoUrl;
        const thumbnail = container.dataset.videoThumbnail;

        // Only delete S3 files for uploaded videos
        const hasVideoToDelete = videoType === 'upload' && videoUrl && !videoUrl.startsWith('http') && !videoUrl.startsWith('data:');
        const hasThumbToDelete = thumbnail && !thumbnail.startsWith('http') && !thumbnail.startsWith('data:');

        if (!hasVideoToDelete && !hasThumbToDelete) return;
        if (typeof deleteVideoFileURL === 'undefined') return;

        const formData = new FormData();
        if (hasVideoToDelete) formData.append('video_path', videoUrl);
        if (hasThumbToDelete) formData.append('thumbnail_path', thumbnail);

        const xhr = new XMLHttpRequest();
        xhr.open('POST', deleteVideoFileURL, true);
        const csrfToken = document.querySelector('meta[name="csrf-token"]');
        if (csrfToken) {
            xhr.setRequestHeader('X-CSRF-Token', csrfToken.getAttribute('content'));
        }
        xhr.send(formData);
    }

    /**
     * Stop all currently playing videos and YouTube iframes on the page
     */
    stopAllPlayingVideos() {
        // Pause all HTML5 video elements
        document.querySelectorAll('.video-container video').forEach(video => {
            video.pause();
        });
        // Stop YouTube iframes by removing src and restoring it
        document.querySelectorAll('.video-container iframe').forEach(iframe => {
            const src = iframe.src;
            iframe.src = '';
            iframe.src = src;
        });
    }

    /**
     * Handle clicking play button on thumbnail to load video/iframe
     */
    handleThumbnailPlay() {
        window.addEventListener('click', (e) => {
            const playBtn = e.target.closest('.video-play-btn');
            if (!playBtn) return;
            e.preventDefault();

            const player = playBtn.closest('.video-player--thumbnail');
            if (!player) return;

            // Stop any other videos before starting the new one
            this.stopAllPlayingVideos();

            const embedUrl = player.dataset.embedUrl;
            const videoSrc = player.dataset.videoSrc;

            if (embedUrl) {
                // YouTube — replace with iframe
                player.classList.remove('video-player--thumbnail');
                player.removeAttribute('data-embed-url');
                player.innerHTML = `<iframe src="${embedUrl}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>`;
            } else if (videoSrc) {
                // Uploaded video — replace with video element
                player.classList.remove('video-player--thumbnail');
                player.removeAttribute('data-video-src');
                player.innerHTML = `<video controls autoplay><source src="${videoSrc}" type="video/mp4"></video>`;
            }
        });
    }

    /**
     * Handle thumbnail upload from settings sidebar
     */
    handleThumbnailUpload() {
        window.addEventListener('click', (e) => {
            const btn = e.target.closest('.video-thumbnail-upload-btn');
            if (!btn) return;
            e.preventDefault();
            const fileInput = btn.closest('.video-settings-thumbnail').querySelector('.video-thumbnail-file-input');
            fileInput.click();
        });

        window.addEventListener('change', (e) => {
            const fileInput = e.target.closest('.video-thumbnail-file-input');
            if (!fileInput) return;

            const file = fileInput.files[0];
            if (!file) return;

            const settingsMenu = document.getElementById('settings-menu');
            const componentId = settingsMenu.dataset.component;
            const component = document.getElementById(componentId);
            if (!component) return;

            const container = component.querySelector('.video-container');
            const reader = new FileReader();
            reader.onload = () => {
                container.dataset.videoThumbnail = reader.result;

                // Update preview in settings
                const preview = settingsMenu.querySelector('.video-thumbnail-preview img');
                if (preview) {
                    preview.src = reader.result;
                    settingsMenu.querySelector('.video-thumbnail-preview').style.display = 'block';
                    settingsMenu.querySelector('.video-thumbnail-remove-btn').style.display = 'flex';
                }

                // Re-render the video player with new thumbnail
                this.refreshVideoPlayer(container);
                autoSave.init(null, 1000);
            };
            reader.readAsDataURL(file);
        });
    }

    /**
     * Handle removing custom thumbnail from settings
     */
    handleThumbnailRemove() {
        window.addEventListener('click', (e) => {
            const btn = e.target.closest('.video-thumbnail-remove-btn');
            if (!btn) return;
            e.preventDefault();

            const settingsMenu = document.getElementById('settings-menu');
            const componentId = settingsMenu.dataset.component;
            const component = document.getElementById(componentId);
            if (!component) return;

            const container = component.querySelector('.video-container');
            container.dataset.videoThumbnail = '';

            // Update settings preview
            const preview = settingsMenu.querySelector('.video-thumbnail-preview');
            if (preview) {
                preview.style.display = 'none';
            }
            btn.style.display = 'none';

            this.refreshVideoPlayer(container);
            autoSave.init(null, 1000);
        });
    }

    /**
     * Handle caption edit from settings sidebar textarea and inline contenteditable
     */
    handleCaptionEdit() {
        let captionDebounce = null;

        window.addEventListener('input', (e) => {
            // Handle settings sidebar textarea → sync to inline caption
            const textarea = e.target.closest('.video-settings-caption-input');
            if (textarea) {
                const settingsMenu = document.getElementById('settings-menu');
                const componentId = settingsMenu.dataset.component;
                const component = document.getElementById(componentId);
                if (!component) return;

                const caption = component.querySelector('.video-container_caption');
                if (caption) {
                    caption.textContent = textarea.value;
                }

                clearTimeout(captionDebounce);
                captionDebounce = setTimeout(() => {
                    autoSave.init(null, 0);
                }, 1000);
                return;
            }

            // Handle inline contenteditable caption edit
            const caption = e.target.closest('.video-container_caption');
            if (caption) {
                clearTimeout(captionDebounce);
                captionDebounce = setTimeout(() => {
                    autoSave.init(null, 0);
                }, 1000);
            }
        });
    }

    /**
     * Refresh video player with current data attributes (after thumbnail change)
     */
    refreshVideoPlayer(container) {
        const videoType = container.dataset.videoType;
        const videoUrl = container.dataset.videoUrl;
        const thumbnail = container.dataset.videoThumbnail;

        if (videoType !== 'youtube' || !videoUrl) return;

        const figure = container.querySelector('.video-container_figure');
        const caption = figure.querySelector('.video-container_caption');
        const captionHtml = caption ? caption.outerHTML : '';
        const embedUrl = convertToYoutubeEmbed(videoUrl);
        const videoId = extractYoutubeId(videoUrl);

        let thumbSrc = thumbnail || (videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : '');

        let playerHtml = '';
        if (thumbSrc) {
            playerHtml = `<div class="video-player video-player--thumbnail" data-embed-url="${embedUrl}">
                <img class="video-thumbnail" src="${thumbSrc}" alt="Video thumbnail">
                <button class="video-play-btn" aria-label="Play video"></button>
            </div>`;
        } else {
            playerHtml = `<div class="video-player">
                <iframe src="${embedUrl}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>
            </div>`;
        }

        figure.innerHTML = `
            ${playerHtml}
            <div class="video-controls-overlay">
                <button class="btn-icon video-remove-btn" title="Replace video"><i class="icon icon-trash"></i></button>
            </div>
            ${captionHtml}`;
    }

    /**
     * Set YouTube video in the container
     */
    setYoutubeVideo(container, url) {
        const embedUrl = convertToYoutubeEmbed(url);
        const videoId = extractYoutubeId(url);
        const figure = container.querySelector('.video-container_figure');
        const caption = figure.querySelector('.video-container_caption');
        const captionText = caption ? caption.textContent : '';
        const thumbnail = container.dataset.videoThumbnail || '';

        container.dataset.videoType = 'youtube';
        container.dataset.videoUrl = url;

        // Use default YT thumbnail
        let thumbSrc = thumbnail || (videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : '');

        let playerHtml = '';
        if (thumbSrc) {
            playerHtml = `<div class="video-player video-player--thumbnail" data-embed-url="${embedUrl}">
                <img class="video-thumbnail" src="${thumbSrc}" alt="Video thumbnail">
                <button class="video-play-btn" aria-label="Play video"></button>
            </div>`;
        } else {
            playerHtml = `<div class="video-player">
                <iframe src="${embedUrl}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>
            </div>`;
        }

        figure.innerHTML = `
            ${playerHtml}
            <div class="video-controls-overlay">
                <button class="btn-icon video-remove-btn" title="Replace video"><i class="icon icon-trash"></i></button>
            </div>
            <figcaption class="video-container_caption" placeholder="${l10n.editor.videoPlaceholder || 'Video caption (optional)'}" contenteditable="true">${captionText}</figcaption>`;
    }

    /**
     * Set uploaded video in the container — with thumbnail if available
     * @param {HTMLElement} container
     * @param {string} signedUrl - signed CloudFront URL for immediate playback
     * @param {string} videoPath - relative S3 path for saving to DB
     */
    setUploadedVideo(container, signedUrl, videoPath) {
        const figure = container.querySelector('.video-container_figure');
        const caption = figure.querySelector('.video-container_caption');
        const captionText = caption ? caption.textContent : '';
        const thumbnail = container.dataset.videoThumbnail || '';

        container.dataset.videoType = 'upload';
        container.dataset.videoUrl = videoPath || signedUrl; // store path for saving

        let playerHtml = '';
        if (thumbnail) {
            playerHtml = `<div class="video-player video-player--thumbnail" data-video-src="${signedUrl}">
                <img class="video-thumbnail" src="${thumbnail}" alt="Video thumbnail">
                <button class="video-play-btn" aria-label="Play video"></button>
            </div>`;
        } else {
            playerHtml = `<div class="video-player">
                <video controls>
                    <source src="${signedUrl}" type="video/mp4">
                </video>
            </div>`;
        }

        figure.innerHTML = `
            ${playerHtml}
            <div class="video-controls-overlay">
                <button class="btn-icon video-remove-btn" title="Replace video"><i class="icon icon-trash"></i></button>
            </div>
            <figcaption class="video-container_caption" placeholder="${l10n.editor.videoPlaceholder || 'Video caption (optional)'}" contenteditable="true">${captionText}</figcaption>`;
    }

    /**
     * Generate thumbnail frames from a video file using canvas
     * @param {File} file - Video file
     * @returns {Promise<string[]>} - Array of data URL thumbnails
     */
    generateVideoThumbnails(file) {
        return new Promise((resolve) => {
            const video = document.createElement('video');
            video.preload = 'metadata';
            video.muted = true;
            video.playsInline = true;

            const objectUrl = URL.createObjectURL(file);
            video.src = objectUrl;

            video.addEventListener('loadedmetadata', () => {
                const duration = video.duration;
                // Capture at 10%, 25%, 50%, 75% of duration
                const timestamps = [
                    duration * 0.1,
                    duration * 0.25,
                    duration * 0.5,
                    duration * 0.75
                ];

                const thumbnails = [];
                let idx = 0;

                const captureFrame = () => {
                    if (idx >= timestamps.length) {
                        URL.revokeObjectURL(objectUrl);
                        video.remove();
                        resolve(thumbnails);
                        return;
                    }

                    video.currentTime = timestamps[idx];
                };

                video.addEventListener('seeked', () => {
                    const canvas = document.createElement('canvas');
                    canvas.width = video.videoWidth;
                    canvas.height = video.videoHeight;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                    thumbnails.push(canvas.toDataURL('image/jpeg', 0.8));
                    idx++;
                    captureFrame();
                });

                captureFrame();
            });

            video.addEventListener('error', () => {
                URL.revokeObjectURL(objectUrl);
                resolve([]);
            });
        });
    }

    /**
     * Reset container back to empty placeholder state
     */
    resetToPlaceholder(container) {
        const {pasteYoutubeUrl, uploadVideoFile, videoUrlLabel, videoUrlHint, videoFileHint} = l10n.editor;
        const figure = container.querySelector('.video-container_figure');
        const caption = figure.querySelector('.video-container_caption');
        const captionText = caption ? caption.textContent : '';

        container.dataset.videoType = '';
        container.dataset.videoUrl = '';
        container.dataset.videoThumbnail = '';

        figure.innerHTML = `
            <div class="video-placeholder">
                <div class="video-placeholder_url-section">
                    <div class="cc-settings-input">
                        <span class="cc-settings-input-label">${videoUrlLabel || 'Add URL to video'}</span>
                        <input type="text" class="cc-settings-field video-url-input" placeholder="${pasteYoutubeUrl || 'Please add URL to video'}" />
                        <small>${videoUrlHint || 'Add URL to video on YouTube or Vimeo.'}</small>
                    </div>
                </div>
                <div class="or-hr"><span>or</span><hr></div>
                <div class="video-placeholder_file-section">
                    <input type="file" class="video-file-input" accept="video/mp4,video/x-m4v,video/*" style="display:none;" />
                    <a href="#" class="video-upload-btn video-tutorial-btn"><i class="icon icon-upload"></i>${uploadVideoFile || 'Upload video file'}</a>
                    <small>${videoFileHint || 'Add file in mp4, ogg, mov, webm, wmv. File size not more than 500Mb'}</small>
                </div>
            </div>
            <figcaption class="video-container_caption" placeholder="${l10n.editor.videoPlaceholder || 'Video caption (optional)'}" contenteditable="true">${captionText}</figcaption>`;
    }

    /**
     * Validate YouTube URL
     */
    isValidYoutubeUrl(url) {
        return /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/.test(url);
    }
}
